import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { joinClass, previewClass } from '@/api/classes';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';

import styles from './join-class.module.css';

export function JoinClassPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<{
    id: number;
    name: string;
    teacher_name: string;
    description?: string | null;
  } | null>(null);
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState<{ name: string } | null>(null);
  const [error, setError] = useState('');

  // 通过邀请链接进入时，自动读取 ?code= 并预览班级
  useEffect(() => {
    const urlCode = params.get('code');
    if (urlCode) {
      setCode(urlCode);
      handlePreview(urlCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handlePreview = async (value: string) => {
    setPreviewError('');
    setPreview(null);
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) return;
    setLoading(true);
    try {
      const c = await previewClass(trimmed);
      setPreview({
        id: c.id,
        name: c.name,
        teacher_name: c.teacher_name,
        description: c.description,
      });
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : '邀请码无效');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    setError('');
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) {
      setError('请输入邀请码');
      return;
    }
    setJoining(true);
    try {
      const c = await joinClass(trimmed);
      setJoined({ name: c.name });
    } catch (err) {
      setError(err instanceof Error ? err.message : '加入失败');
    } finally {
      setJoining(false);
    }
  };

  if (joined) {
    return (
      <div className={styles.page}>
        <Card className={styles.card}>
          <div className={styles.successIcon}>✓</div>
          <h1 className={styles.successTitle}>已加入「{joined.name}」</h1>
          <p className={styles.successText}>
            现在你可以在「作业列表」中看到老师布置的作业了。
          </p>
          <div className={styles.actions}>
            <Button onClick={() => navigate('/jobs')}>去写作业</Button>
            <Button variant="secondary" onClick={() => navigate('/my-classes')}>
              查看我的班级
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Card className={styles.card}>
        <h1 className={styles.title}>加入班级</h1>
        <p className={styles.subtitle}>
          三种方式任选其一：① 请老师提供<span className={styles.hl}>邀请码</span>；
          ② 打开老师发来的<span className={styles.hl}>邀请链接</span>；③ 用手机<span className={styles.hl}>扫描二维码</span>。
        </p>

        <label className={styles.label}>邀请码</label>
        <div className={styles.row}>
          <input
            className={styles.input}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="例如 MATH03"
            maxLength={16}
          />
          <Button
            type="button"
            variant="secondary"
            loading={loading}
            onClick={() => handlePreview(code)}
          >
            预览
          </Button>
        </div>

        {previewError && <div className={styles.error}>{previewError}</div>}

        {preview && (
          <div className={styles.preview}>
            <div className={styles.previewName}>{preview.name}</div>
            <div className={styles.previewMeta}>
              班主任：{preview.teacher_name}
              {preview.description ? ` · ${preview.description}` : ''}
            </div>
          </div>
        )}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <Button onClick={handleJoin} loading={joining}>
            加入班级
          </Button>
          <Button variant="secondary" onClick={() => navigate('/my-classes')}>
            我的班级
          </Button>
        </div>
      </Card>
    </div>
  );
}
